import React, { useState } from 'react';
import { Form, Input, Button, Select, message, Card } from 'antd';
import { SaveOutlined, PlusOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { addPost } from '../../api/mockData'; // 只导入 addPost 函数
import './index.css';

const { TextArea } = Input;
const { Option } = Select;

const WritePost = () => {
  const [form] = Form.useForm();
  const [tags, setTags] = useState(['青铜器', '瓷器', '玉器', '书画', '玉器', '陶器', '金银器', '壁画', '古籍', '雕塑']);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const onFinish = async (values) => {
    try {
      setLoading(true);
      
      // 验证数据
      if (!values.title || !values.summary || !values.content) {
        message.error('请填写完整信息');
        return;
      }

      const newPost = {
        id: Date.now().toString(),
        title: values.title.trim(),
        author: '文物守护者',
        date: new Date().toLocaleString('zh-CN', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit'
        }),
        tags: values.tags || ['未分类'],
        summary: values.summary.trim(),
        content: values.content.trim()
      };

      // 添加文章到全局数据
      addPost(newPost);
      
      message.success({
        content: '文物故事发布成功！',
        duration: 2,
        style: {
          fontFamily: '"STKaiti", serif',
        }
      });

      // 重置表单
      form.resetFields();
      
      // 短暂延迟后跳转
      setTimeout(() => {
        navigate(`/post/${newPost.id}`);
      }, 1500);

    } catch (error) {
      console.error('发布文章时出错:', error);
      message.error('发布失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  const onFinishFailed = (errorInfo) => {
    console.log('表单验证失败:', errorInfo);
    message.error('请检查表单填写是否正确');
  };

  const addTag = (tag) => {
    const trimmedTag = tag.trim();
    if (trimmedTag && !tags.includes(trimmedTag)) {
      setTags([...tags, trimmedTag]);
      return trimmedTag;
    }
    return null;
  };

  const handleTagInputKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const value = e.target.value.trim();
      if (value) {
        const newTag = addTag(value);
        if (newTag) {
          const currentTags = form.getFieldValue('tags') || [];
          form.setFieldsValue({
            tags: [...currentTags, newTag]
          });
          e.target.value = '';
          message.success(`已添加新标签: ${newTag}`);
        }
      }
    }
  };

  const handleCancel = () => {
    form.resetFields();
    navigate('/');
  };

  return (
    <div className="write-page">
      <div className="page-header">
        <h2 className="page-title">
          <span className="chinese-seal-small">撰</span>
          撰写文物故事
        </h2>
        <p className="page-subtitle">以文字唤醒沉睡的记忆</p>
      </div>

      <Card className="write-card">
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          onFinishFailed={onFinishFailed}
          className="write-form"
          initialValues={{
            tags: ['青铜器']
          }}
        >
          <Form.Item
            name="title"
            label="文物名称"
            rules={[
              { required: true, message: '请输入文物名称' },
              { min: 2, max: 50, message: '文物名称长度为2-50个字符' },
              { whitespace: true, message: '文物名称不能仅为空格' }
            ]}
            extra="为文物起一个合适的名称，如：司母戊鼎、青花瓷瓶等"
          >
            <Input 
              placeholder="如：司母戊鼎、青花瓷等"
              size="large"
              className="antique-input"
              maxLength={50}
              showCount
            />
          </Form.Item>

          <Form.Item
            name="summary"
            label="文物简介"
            rules={[
              { required: true, message: '请输入文物简介' },
              { min: 10, max: 200, message: '简介长度为10-200个字符' },
              { whitespace: true, message: '简介不能仅为空格' }
            ]}
            extra="用一段简短的文字描述这件文物的历史意义和文化价值"
          >
            <TextArea 
              rows={3} 
              placeholder="用一段话简要描述这件文物的故事、特点和历史价值..."
              className="antique-textarea"
              showCount
              maxLength={200}
            />
          </Form.Item>

          <Form.Item
            name="tags"
            label="文物标签"
            rules={[{ required: true, message: '请选择至少一个标签' }]}
            extra="选择或创建标签，帮助分类和检索"
          >
            <Select
              mode="multiple"
              placeholder="选择或输入文物标签"
              size="large"
              allowClear
              dropdownRender={menu => (
                <>
                  {menu}
                  <div className="add-tag-section">
                    <Input
                      placeholder="输入新标签后按回车添加"
                      onKeyDown={handleTagInputKeyDown}
                      className="tag-input"
                    />
                  </div>
                </>
              )}
              className="tag-select"
            >
              {tags.map(tag => (
                <Option key={tag} value={tag}>
                  <div className="tag-option">
                    <span className="tag-icon">🏷️</span>
                    {tag}
                  </div>
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="content"
            label="详细故事"
            rules={[
              { required: true, message: '请撰写详细文物故事' },
              { min: 50, message: '详细内容至少50个字符' },
              { whitespace: true, message: '内容不能仅为空格' }
            ]}
            extra={
              <div className="markdown-tips">
                <span>支持Markdown格式：</span>
                <span className="tip-item"># 标题</span>
                <span className="tip-item">*斜体*</span>
                <span className="tip-item">**粗体**</span>
                <span className="tip-item">- 列表</span>
                <span className="tip-item">{'>'} 引用</span>
              </div>
            }
          >
            <TextArea 
              rows={12} 
              placeholder="在这里撰写详细的文物故事，包括历史背景、发现过程、文化意义等..."
              className="antique-textarea content-editor"
              showCount
            />
          </Form.Item>

          <Form.Item>
            <div className="form-actions">
              <Button 
                onClick={handleCancel} 
                className="cancel-btn"
                size="large"
                disabled={loading}
              >
                取消
              </Button>
              <Button 
                type="primary" 
                htmlType="submit" 
                icon={<SaveOutlined />}
                size="large"
                className="submit-btn"
                loading={loading}
                disabled={loading}
              >
                {loading ? '发布中...' : '发布文物故事'}
              </Button>
            </div>
            
            <div className="form-tips">
              <p>💡 提示：发布前请仔细检查内容，确保信息准确无误。</p>
              <p>📖 建议：添加详细的描述和高质量的内容能更好地展示文物价值。</p>
            </div>
          </Form.Item>
        </Form>
      </Card>

      {/* 预览区域（可选功能） */}
      <Card className="preview-card">
        <h3 className="preview-title">
          <PlusOutlined /> 内容预览提示
        </h3>
        <div className="preview-content">
          <p>发布后将展示以下信息：</p>
          <ul>
            <li>文物的完整故事内容</li>
            <li>自动生成的发布日期和时间</li>
            <li>您选择的分类标签</li>
            <li>作者信息（当前登录用户）</li>
          </ul>
          <p className="preview-note">
            注意：发布后文章将出现在首页和归档页面，其他用户都可以查看。
          </p>
        </div>
      </Card>
    </div>
  );
};

export default WritePost;